/* eslint-disable no-unused-vars */
import React, { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import "./CommentField.scss";
import { DraggableUpload } from "hooks/useDraggableUpload";
import { CustomFileType } from "hooks/useFileHook";
import FilePreview from "components/v2/FilePreview/FilePreview";
import { prettifyBytes } from "helpers/commonHelpers";
import { ReactComponent as AttachmentIcon } from "assets/images/iconsV2/attachment.svg";
import Button from "components/v2/Button/Button";
import Dropdown from "components/v2/Dropdown/Dropdown";

type BaseProps = {
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  onSubmit: () => void;
  isSubmitting: boolean;
  audienceOptions?: any[];
  isQuickRequest?: boolean;
  isInternalUsersOnly?: boolean;
};

type FileInputProps = {
  dragging: boolean;
  draggableProps: Omit<DraggableUpload, "dragging">;
  onPaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  files: CustomFileType[];
  filesSortedByErrorsFirst: CustomFileType[];
  onFileRemove: (file: CustomFileType) => void;
  maxFileCount: number;
  hiddenFileInputRef: React.RefObject<HTMLInputElement>;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
};
type FileInputEnabledProps = {
  disableFileInput?: false;
} & FileInputProps;

type FileInputDisabledProps = {
  disableFileInput: true;
} & Partial<FileInputProps>;

type CommentFieldProps = BaseProps & (FileInputEnabledProps | FileInputDisabledProps);

const CommentField: FC<CommentFieldProps> = function (props) {
  const {
    files = [],
    filesSortedByErrorsFirst = [],
    dragging,
    draggableProps,
    onPaste,
    onFileRemove,
    maxFileCount = 10,
    hiddenFileInputRef,
    onFileUpload,

    disableFileInput = false,

    textAreaRef,
    onSubmit,
    isSubmitting,
    audienceOptions,
    isQuickRequest,
    isInternalUsersOnly = false,
  } = props;

  const intl = useIntl();

  const filesExceedMaxCount = files.length > maxFileCount;
  const hasError = files.some((file) => file.isError) || filesExceedMaxCount;
  const hasUploadingOrDeleting = files.some((file) => file.isUploading || file.isDeleting) || filesExceedMaxCount;

  return (
    <div className="comment-field-wrapper mb-3xl">
      <div className={`comment-field ${dragging ? "comment-field--dragging" : ""}`} {...draggableProps}>
        <div className="comment-field--body">
          <textarea
            ref={textAreaRef}
            placeholder={
              disableFileInput
                ? intl.formatMessage({
                    id: "general.writeAComment",
                    defaultMessage: "Write a comment.",
                  })
                : intl.formatMessage({
                    id: "tracking.activityField.placeholder",
                    defaultMessage: "Add a comment or drop/paste files.",
                  })
            }
            onPaste={onPaste}
          />

          {filesSortedByErrorsFirst.length > 0 && (
            <div className="comment-field--attachments">
              {filesSortedByErrorsFirst.map((file) => (
                <FilePreview
                  key={file.id}
                  fileName={file.file.name}
                  fileSize={prettifyBytes(file.file.size)}
                  url={file.url}
                  isLoading={file.isUploading || file.isDeleting}
                  isError={file.isError}
                  errorMessage={file.errorMessage}
                  onClose={() => onFileRemove?.(file)}
                />
              ))}
            </div>
          )}
          {filesExceedMaxCount && (
            <p className="m-0 text-xs text-error-primary">
              <FormattedMessage id="general.fileCountExceedsError" defaultMessage="Upload failed. File count exceeds 10." />
            </p>
          )}
        </div>

        <div className="comment-field__footer py-lg px-xl d-flex justify-content-between">
          {files.length < maxFileCount && !disableFileInput ? (
            <div className="p-md cursor-pointer" onClick={() => hiddenFileInputRef?.current?.click()}>
              <AttachmentIcon />
              <input
                multiple
                type="file"
                style={{ display: "none" }}
                ref={hiddenFileInputRef}
                onChange={onFileUpload}
                onClick={(event: any) => {
                  event.target.value = null;
                }}
              />
            </div>
          ) : (
            <div />
          )}

          <div className="comment-field__actions d-flex align-items-center">
            {audienceOptions && !isQuickRequest && (
              <div className="comment-field__audience d-flex align-items-center">
                <div className="text-nowrap mr-xs">
                  <FormattedMessage id="quote-request.comments.visibleTo" defaultMessage="Visible to" />
                </div>
                {isInternalUsersOnly ? (
                  <span className="comment-field__audience-label text-nowrap">
                    <FormattedMessage id="quote-request.comments.internalUsers" defaultMessage="Internal users" />
                  </span>
                ) : (
                  <Dropdown
                    options={audienceOptions}
                    name="selected_message_audience"
                    variant="outline"
                    autoFlip
                    wrapMenu
                    alignRight
                    closeWhenOffscreen
                  />
                )}
              </div>
            )}
            <Button
              label={intl.formatMessage({
                id: "general.comment",
                defaultMessage: "Comment",
              })}
              onClick={onSubmit}
              disabled={hasError || hasUploadingOrDeleting || isSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentField;

CommentField.defaultProps = {
  audienceOptions: undefined,
};
